/**
 * DbWaterfallProductCard - 瀑布流商品卡片组件
 *
 * 特性：
 * - 专为双列瀑布流布局设计
 * - 图片高度为宽度的 85%（非正方形）
 * - 支持标题、标签、价格、销量、购物车按钮
 * - 使用 db-rn-ui 组件构建
 */

import React from 'react';
import {
  View,
  Image,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
} from 'react-native';
import { DbProductTitle } from '@/components/DbProductTitle';
import { DbTag } from '@/components/DbTag';
import { DbAmountDisplay } from '@/components/DbAmountDisplay';
import { DbTitlePair } from '@/components/DbTitlePair';
import DbIcon from '@/components/DbIcon';

const { width } = Dimensions.get('window');
const CARD_WIDTH = (width - 32) / 2; // 两列，减去左右边距和中间间隙
const IMAGE_HEIGHT = CARD_WIDTH * 0.85; // 图片高度为宽度的85%

export interface DbWaterfallProductCardProps {
  /** 商品图片 URL */
  image: string;
  /** 商品标题 */
  title: string;
  /** 当前价格 */
  price: number;
  /** 原价（可选） */
  originalPrice?: number;
  /** 销量文字，如 "已售 100" */
  salesText?: string;
  /** 标签列表 */
  tags?: Array<{
    text: string;
    preset?: 'free-shipping' | 'guarantee' | 'hot' | 'new' | 'default';
    variant?: 'solid' | 'outline' | 'light' | 'text';
    color?: string;
  }>;
  /** 点击卡片回调 */
  onPress?: () => void;
  /** 点击购物车按钮回调 */
  onCartPress?: () => void;
  /** 标题前缀标签（如热销、新品） */
  titlePrefixTags?: Array<{
    text: string;
    color?: string;
    outline?: boolean;
  }>;
  /** 卡片圆角，默认 12 */
  borderRadius?: number;
  /** 图片圆角（默认为 borderRadius） */
  imageBorderRadius?: number;
  /** 标题是否显示为已点击状态（灰色） */
  titleVisited?: boolean;
  /** 已点击状态的标题颜色，默认 #999 */
  visitedTitleColor?: string;
  /** 自定义内容区域（优先于默认内容） */
  customContent?: React.ReactNode;
}

export const DbWaterfallProductCard: React.FC<DbWaterfallProductCardProps> = ({
  image,
  title,
  price,
  originalPrice,
  salesText,
  tags = [],
  onPress,
  onCartPress,
  titlePrefixTags = [{ text: '热销', color: '#FF2442', outline: true }],
  borderRadius = 12,
  imageBorderRadius,
  titleVisited = false,
  visitedTitleColor = '#999',
  customContent,
}) => {
  // 图片圆角默认为卡片圆角
  const imgBorderRadius = imageBorderRadius ?? borderRadius;
  // 处理购物车点击，阻止事件冒泡
  const handleCartPress = (e: any) => {
    e.stopPropagation?.();
    onCartPress?.();
  };

  return (
    <TouchableOpacity
      style={[styles.container, { borderRadius }]}
      onPress={onPress}
      activeOpacity={0.9}
    >
      {/* 商品图片 */}
      <Image
        source={{ uri: image }}
        style={[
          styles.image,
          {
            borderTopLeftRadius: imgBorderRadius,
            borderTopRightRadius: imgBorderRadius,
          },
        ]}
        resizeMode="cover"
      />

      {/* 内容区域 */}
      <View style={styles.content}>
        {customContent ? (
          // 自定义内容（优先）
          customContent
        ) : (
          // 默认内容
          <>
            {/* 1. 商品标题 - 带前缀标签 */}
            <DbProductTitle.Inline
              title={title}
              numberOfLines={2}
              fontSize={14}
              color={titleVisited ? visitedTitleColor : '#333'}
              fontWeight="500"
              lineHeight={20}
              inlineTags={titlePrefixTags}
              tagGap={6}
              style={styles.title}
            />

            {/* 2. 标签区域 */}
            <View style={styles.tagRow}>
              {tags.map((tag, index) => (
                <DbTag
                  key={index}
                  text={tag.text}
                  preset={tag.preset || 'default'}
                  variant={tag.variant || 'light'}
                  size="mini"
                  shape="rounded"
                  color={tag.color}
                />
              ))}
            </View>

            {/* 3. 价格区域 */}
            <View style={styles.priceSection}>
              <DbAmountDisplay
                amount={price}
                currency="¥"
                mainFontSize={18}
                mainColor="#FF2442"
                mainFontWeight="700"
                subFontSize={12}
                originalAmount={originalPrice}
                originalColor="#999999"
                originalFontSize={12}
                originalGap={8}
              />
            </View>

            {/* 4. 底部信息 - 销量 + 购物车 */}
            <DbTitlePair
              title={{
                text: salesText || '',
                fontSize: 11,
                color: '#999',
              }}
              subtitle={{
                content: onCartPress ? (
                  <TouchableOpacity
                    onPress={handleCartPress}
                    activeOpacity={0.7}
                    style={styles.cartButton}
                  >
                    <DbIcon name="cart-outline" size={20} color="#FF2442" />
                  </TouchableOpacity>
                ) : null,
              }}
              layout="space-between"
              verticalAlign="center"
              style={styles.bottomSection}
            />
          </>
        )}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    backgroundColor: '#fff',
    // borderRadius 通过 props 传入
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 3,
  },
  image: {
    width: '100%',
    height: IMAGE_HEIGHT,
    resizeMode: 'cover',
    // borderTopLeftRadius 和 borderTopRightRadius 通过 props 传入
  },
  content: {
    padding: 10,
    paddingBottom: 6,
  },
  title: {
    marginBottom: 6,
  },
  // 标签行样式 - 使用 flex-end 对齐，让不同样式的标签底部对齐
  tagRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: 6,
    marginTop:4,
    marginBottom:2
  },
  // 价格区域样式
  priceSection: {
    marginBottom: 2,
  },
  // 底部区域样式
  bottomSection: {
    // marginTop: 4,
  },
  // 购物车按钮
  cartButton: {
    // padding: 4,
  },
});

export default DbWaterfallProductCard;
